import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { getFormattedDateTime } from "@/utils";
import { AIInsightsPanel } from "@/components/analytics/ai-insights-panel";
import {
  Search,
  Filter,
  FileText,
  Download,
  RotateCcw,
  ChevronDown,
  CreditCard,
  Calendar,
  Building2,
  DollarSign,
  Tag,
  Clock,
  User,
  Network,
  ExternalLink,
  BarChart3,
} from "lucide-react";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";

export default function PerkPointTransactions() {
  const { toast } = useToast();

  // Sample transaction data - in a real app this would come from an API
  const allTransactions = [
    {
      id: "TX-2345-1",
      memberId: "MEM-001",
      memberName: "Alex Johnson",
      program: "Leadership Training",
      action: "Purchase Reward",
      points: "+500",
      date: "2025-05-19",
      time: "14:32:21",
      type: "earn",
      status: "completed",
      merchant: "Starbucks",
      currency: "USD",
      amount: 42.5,
      blockchainTxId: "0x7a58c0340a8b42c323edgfe852f651e047fbfa7a2a52689fe715a",
      blockchainExplorerUrl:
        "https://etherscan.io/tx/0x7a58c0340a8b42c323edgfe852f651e047fbfa7a2a52689fe715a",
      metadata: {
        programId: "PRG-001",
        merchantId: "MER-101",
        merchantLocation: "Downtown Branch",
        purchasedItems: [
          { name: "Coffee", quantity: 2, price: 4.5 },
          { name: "Sandwich", quantity: 1, price: 8.75 },
        ],
        earnReason: "Purchase with loyalty card",
        earnMultiplier: "2.5x",
        loyaltyCardUsed: "Yes",
        blockchainNetwork: "Ethereum Mainnet",
        tokenStandard: "ERC-20",
        blockConfirmations: "153",
      },
    },
    {
      id: "TX-2345-2",
      memberId: "MEM-002",
      memberName: "Sam Smith",
      program: "Customer Service 101",
      action: "Return Refund",
      points: "+100",
      date: "2025-05-18",
      time: "09:15:43",
      type: "earn",
      status: "completed",
      merchant: "Nike",
      currency: "USD",
      amount: 125.0,
      blockchainTxId:
        "0x9b67d430ab5c23edcc835629db035c7f9235ffa35664be3d1d6f617",
      blockchainExplorerUrl:
        "https://etherscan.io/tx/0x9b67d430ab5c23edcc835629db035c7f9235ffa35664be3d1d6f617",
      metadata: {
        programId: "PRG-002",
        merchantId: "MER-202",
        merchantLocation: "Mall of America",
        purchasedItems: [{ name: "Running Shoes", quantity: 1, price: 125.0 }],
        earnReason: "Post-return customer satisfaction bonus",
        loyaltyTier: "Silver",
        blockchainNetwork: "Ethereum Mainnet",
        tokenStandard: "ERC-20",
        blockConfirmations: "167",
      },
    },
    {
      id: "TX-2345-3",
      memberId: "MEM-003",
      memberName: "Taylor Williams",
      program: "Sales Techniques",
      action: "Referral Bonus",
      points: "+250",
      date: "2025-05-17",
      time: "15:41:29",
      type: "earn",
      status: "completed",
      merchant: "Best Buy",
      currency: "USD",
      amount: 65.75,
      blockchainTxId:
        "0x2c53d1c98a705f5123d156cd4f40c6fba713eb49be8e3d1226fe513",
      blockchainExplorerUrl:
        "https://etherscan.io/tx/0x2c53d1c98a705f5123d156cd4f40c6fba713eb49be8e3d1226fe513",
      metadata: {
        programId: "PRG-003",
        referralCode: "REF-TW-2025",
        referredMember: "Jamie Lee",
        referredMemberId: "MEM-004",
        referralPurchaseAmount: 399.99,
        blockchainNetwork: "Ethereum Mainnet",
        tokenStandard: "ERC-20",
        blockConfirmations: "246",
      },
    },
    {
      id: "TX-2345-4",
      memberId: "MEM-001",
      memberName: "Alex Johnson",
      program: "Premium Rewards",
      action: "Gift Card Redemption",
      points: "-1000",
      date: "2025-05-16",
      time: "11:28:55",
      type: "burn",
      status: "completed",
      merchant: "Amazon",
      currency: "USD",
      amount: 50.0,
      blockchainTxId:
        "0x8d45c17a0ab6c2d9e254f92c3d42a3e13bcc23ff4b45e7c181e5432",
      blockchainExplorerUrl:
        "https://etherscan.io/tx/0x8d45c17a0ab6c2d9e254f92c3d42a3e13bcc23ff4b45e7c181e5432",
      metadata: {
        programId: "PRG-004",
        rewardId: "RWD-101",
        rewardTitle: "$50 Amazon Gift Card",
        deliveryMethod: "Email",
        deliveryEmail: "alex.johnson@example.com",
        fulfilledDate: "2025-05-16",
        giftCardCode: "AMZN-XXXX-XXXX",
        blockchainNetwork: "Ethereum Mainnet",
        tokenStandard: "ERC-20",
        blockConfirmations: "312",
      },
    },
    {
      id: "TX-2345-5",
      memberId: "MEM-004",
      memberName: "Jamie Lee",
      program: "Professional Development",
      action: "Welcome Bonus",
      points: "+300",
      date: "2025-05-15",
      time: "13:09:37",
      type: "earn",
      status: "pending",
      merchant: "Target",
      currency: "USD",
      amount: 75.0,
      blockchainTxId:
        "0x5f12c0e5ab36cdf1298a6d92b254ebd0c983ae72f5341b955c7d065",
      blockchainExplorerUrl:
        "https://etherscan.io/tx/0x5f12c0e5ab36cdf1298a6d92b254ebd0c983ae72f5341b955c7d065",
      metadata: {
        programId: "PRG-005",
        merchantId: "MER-303",
        merchantLocation: "Uptown Branch",
        purchasedItems: [{ name: "Household Items", quantity: 5, price: 15.0 }],
        promotionCode: "WELCOME25",
        bonusMultiplier: "3x",
        blockchainNetwork: "Ethereum Mainnet",
        tokenStandard: "ERC-20",
        pendingReason: "Awaiting blockchain confirmation",
        blockConfirmations: "4",
      },
    },
    {
      id: "TX-2345-6",
      memberId: "MEM-002",
      memberName: "Sam Smith",
      program: "Loyalty Points",
      action: "Product Redemption",
      points: "-500",
      date: "2025-05-15",
      time: "10:14:52",
      type: "burn",
      status: "failed",
      merchant: "Apple",
      currency: "USD",
      amount: 249.99,
      blockchainTxId:
        "0x1a3b5c7d9e0f2468ace0145678901234567890abcdef0123456789",
      blockchainExplorerUrl:
        "https://etherscan.io/tx/0x1a3b5c7d9e0f2468ace0145678901234567890abcdef0123456789",
      metadata: {
        programId: "PRG-006",
        rewardId: "RWD-202",
        rewardTitle: "Premium Headphones",
        failureReason: "Insufficient points balance",
        requiredPoints: "750",
        actualPoints: "500",
        attemptDate: "2025-05-15",
        blockchainNetwork: "Ethereum Mainnet",
        tokenStandard: "ERC-20",
        blockchainError: "Transaction reverted: insufficient token balance",
      },
    },
  ];

  // Available programs (should be fetched from API in real app)
  const availablePrograms = [
    "Leadership Training",
    "Customer Service 101",
    "Sales Techniques",
    "Premium Rewards",
    "Professional Development",
    "Loyalty Points",
  ];

  // State for filters and pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [programFilter, setProgramFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Get default programId from localStorage if available
  useEffect(() => {
    const savedProgramId = localStorage.getItem("selectedProgramId");
    if (savedProgramId) {
      setProgramFilter(savedProgramId);
    }
  }, []);

  // Apply filters
  const filteredTransactions = allTransactions.filter((transaction) => {
    // Search query filter
    const matchesSearch =
      searchQuery === "" ||
      transaction.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.memberId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.memberName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      transaction.program.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.merchant.toLowerCase().includes(searchQuery.toLowerCase());

    // Program filter
    const matchesProgram =
      programFilter === "all" || transaction.program === programFilter;

    // Type filter
    const matchesType = typeFilter === "all" || transaction.type === typeFilter;

    // Status filter
    const matchesStatus =
      statusFilter === "all" || transaction.status === statusFilter;

    return matchesSearch && matchesProgram && matchesType && matchesStatus;
  });

  // Calculate paginated data
  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const currentTransactions = filteredTransactions.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);

  // Reset filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setProgramFilter("all");
    setTypeFilter("all");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  // Export transactions as CSV
  const handleExportCSV = () => {
    // Create CSV header
    let csv =
      "Transaction ID,Date,Time,Member ID,Member Name,Program,Action,Points,Type,Status,Merchant,Amount,Currency,Blockchain Transaction ID\n";

    // Add rows for each filtered transaction
    filteredTransactions.forEach((tx) => {
      const row = [
        tx.id,
        tx.date,
        tx.time,
        tx.memberId,
        tx.memberName,
        tx.program,
        tx.action,
        tx.points,
        tx.type,
        tx.status,
        tx.merchant,
        tx.amount,
        tx.currency,
        tx.blockchainTxId,
      ]
        .map((value) => `"${value}"`)
        .join(",");

      csv += row + "\n";
    });

    // Create download link
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    // Set link properties
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `loyalty_transactions_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";

    // Add to document, trigger download and clean up
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Export successful",
      description: `${filteredTransactions.length} transactions exported as CSV.`,
    });
  };

  // View transaction details
  const handleViewDetails = (transaction: any) => {
    setSelectedTransaction(transaction);
    setIsDetailsModalOpen(true);
  };

  // Handle program filter change
  const handleProgramChange = (value: string) => {
    setProgramFilter(value);

    // Save selected program to localStorage if it's not "all"
    if (value !== "all") {
      localStorage.setItem("selectedProgramId", value);
    } else {
      localStorage.removeItem("selectedProgramId");
    }
  };

  const transactionSampleQuestions = [
    "What's the total transaction volume?",
    "Show me transaction trends by time",
    "Which merchants perform best?",
    "What's the points earn vs burn ratio?",
    "Analyze transaction patterns",
    "Show me revenue insights",
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Loyalty Transactions
          </h1>
          <p className="text-muted-foreground">
            View and manage loyalty program transactions.
          </p>
        </div>
        <Button onClick={handleExportCSV} className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          <span>Export CSV</span>
        </Button>
      </div>

      {/* Main content with tabs */}
      <Tabs defaultValue="transactions" className="w-full">
        <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-2 h-auto">
          <TabsTrigger value="transactions" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Transactions
          </TabsTrigger>
          <TabsTrigger value="ai-insights" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            AI Insights
          </TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="mt-6 space-y-6">
          {/* Search and filters */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-5">
                  <div className="relative">
                    <Input
                      placeholder="Search by ID, member name, or merchant..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Select
                    value={programFilter}
                    onValueChange={handleProgramChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All Programs" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Programs</SelectItem>
                      {availablePrograms.map((program) => (
                        <SelectItem key={program} value={program}>
                          {program}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="earn">Points Earned</SelectItem>
                      <SelectItem value="burn">Points Redeemed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-1">
                  <Button
                    variant="outline"
                    onClick={handleResetFilters}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    <span className="sr-only md:not-sr-only">Reset</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transactions table */}
          <Card>
            <CardHeader>
              <CardTitle>Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transaction ID</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Points</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No transactions found matching your filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            {transaction.id}
                            <span className="text-xs text-blue-500 hover:text-blue-700 cursor-pointer flex items-center">
                              <Network className="h-3 w-3 mr-1" />
                              <a
                                href={transaction.blockchainExplorerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="truncate max-w-[80px]"
                              >
                                {transaction.blockchainTxId.substring(0, 8)}...
                              </a>
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getFormattedDateTime(
                            `${transaction.date}T${transaction.time}`,
                          )}
                        </TableCell>
                        <TableCell>
                          {transaction.memberName}
                          <br />
                          <span className="text-xs text-muted-foreground">
                            {transaction.memberId}
                          </span>
                        </TableCell>
                        <TableCell>{transaction.merchant}</TableCell>
                        <TableCell>{transaction.program}</TableCell>
                        <TableCell
                          className={
                            transaction.type === "earn"
                              ? "text-green-600 font-medium"
                              : "text-red-600 font-medium"
                          }
                        >
                          {transaction.points}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              transaction.status === "completed"
                                ? "bg-green-100 text-green-800 hover:bg-green-100"
                                : transaction.status === "pending"
                                ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
                                : "bg-red-100 text-red-800 hover:bg-red-100"
                            }
                          >
                            {transaction.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(transaction)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {filteredTransactions.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Rows per page:</span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={(value) => {
                    setRowsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <EnhancedPaginationControl
                totalPages={totalPages}
                currentPage={currentPage - 1} // Convert to 0-based index
                onPageChange={(page) => setCurrentPage(page + 1)} // Convert back to 1-based index
              />
              <div className="w-[160px]" /> {/* Spacer to balance layout */}
            </div>
          )}
        </TabsContent>

        <TabsContent value="ai-insights" className="mt-6">
          <AIInsightsPanel
            title="Transaction AI Analytics"
            dataType="transactions"
            sampleQuestions={transactionSampleQuestions}
            data={filteredTransactions}
          />
        </TabsContent>
      </Tabs>

      {/* Transaction Details Modal */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="max-w-md p-0">
          {selectedTransaction && (
            <>
              <DialogHeader className="p-6 pb-2 bg-gray-50 border-b">
                <DialogTitle className="text-xl">
                  Transaction Details
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedTransaction.id}
                </p>
              </DialogHeader>

              <ScrollArea className="max-h-[60vh]">
                <div className="p-6">
                  {/* Main transaction info */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-4 rounded-md">
                      <div className="text-xs text-muted-foreground">
                        Points
                      </div>
                      <div
                        className={`text-xl font-semibold ${
                          selectedTransaction.type === "earn"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {selectedTransaction.points}
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-md">
                      <div className="text-xs text-muted-foreground">
                        Amount
                      </div>
                      <div className="text-lg font-semibold">
                        {selectedTransaction.currency}{" "}
                        {selectedTransaction.amount}
                      </div>
                    </div>
                  </div>

                  {/* Blockchain information */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium mb-2">
                      Blockchain Information
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <div className="flex items-center justify-between py-2 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                          <Network className="h-4 w-4 text-gray-600" />
                          <span className="text-sm">Transaction ID</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-xs truncate max-w-[140px]">
                            {selectedTransaction.blockchainTxId.substring(
                              0,
                              10
                            )}
                            ...
                          </span>
                          <a
                            href={selectedTransaction.blockchainExplorerUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 text-blue-500 hover:text-blue-700"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>

                      {selectedTransaction.metadata.blockchainNetwork && (
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <Network className="h-4 w-4 text-gray-600" />
                            <span className="text-sm">Network</span>
                          </div>
                          <div className="text-sm font-medium">
                            {selectedTransaction.metadata.blockchainNetwork}
                          </div>
                        </div>
                      )}

                      {selectedTransaction.metadata.tokenStandard && (
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <Tag className="h-4 w-4 text-gray-600" />
                            <span className="text-sm">Token Standard</span>
                          </div>
                          <div className="text-sm font-medium">
                            {selectedTransaction.metadata.tokenStandard}
                          </div>
                        </div>
                      )}

                      {selectedTransaction.metadata.blockConfirmations && (
                        <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-600" />
                            <span className="text-sm">Confirmations</span>
                          </div>
                          <div className="text-sm font-medium">
                            {selectedTransaction.metadata.blockConfirmations}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Basic information */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium mb-2">
                      Transaction Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">Date & Time</span>
                        </div>
                        <div className="text-sm font-medium">
                          {getFormattedDateTime(
                            `${selectedTransaction.date}T${selectedTransaction.time}`,
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">Member</span>
                        </div>
                        <div className="text-sm font-medium">
                          {selectedTransaction.memberName} (
                          {selectedTransaction.memberId})
                        </div>
                      </div>

                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">Merchant</span>
                        </div>
                        <div className="text-sm font-medium">
                          {selectedTransaction.merchant}
                        </div>
                      </div>

                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">Program</span>
                        </div>
                        <div className="text-sm font-medium">
                          {selectedTransaction.program}
                        </div>
                      </div>

                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">Action</span>
                        </div>
                        <div className="text-sm font-medium">
                          {selectedTransaction.action}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Purchase items if available */}
                  {selectedTransaction.metadata.purchasedItems && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium mb-2">
                        Purchased Items
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-md">
                        {selectedTransaction.metadata.purchasedItems.map(
                          (
                            item: {
                              name: string;
                              quantity: number;
                              price: number;
                            },
                            index: number
                          ) => (
                            <div
                              key={index}
                              className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                            >
                              <div className="text-sm">
                                {item.name} x{item.quantity}
                              </div>
                              <div className="text-sm font-medium">
                                {selectedTransaction.currency}{" "}
                                {item.price.toFixed(2)}
                              </div>
                            </div>
                          )
                        )}

                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Total</span>
                            <span className="text-sm font-medium">
                              {selectedTransaction.currency}{" "}
                              {selectedTransaction.amount}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Additional metadata */}
                  <div>
                    <h3 className="text-sm font-medium mb-2">
                      Additional Details
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-md">
                      {Object.entries(selectedTransaction.metadata)
                        .filter(
                          ([key]) =>
                            ![
                              "purchasedItems",
                              "blockchainNetwork",
                              "blockConfirmations",
                              "tokenStandard",
                            ].includes(key)
                        )
                        .map(([key, value]) => (
                          <div
                            key={key}
                            className="flex justify-between py-2 border-b border-gray-100 last:border-0"
                          >
                            <div className="text-sm text-gray-600">
                              {key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) =>
                                  str.toString().toUpperCase()
                                )}
                            </div>
                            <div className="text-sm font-medium">
                              {/* Safely render value as string */}
                              {String(value)}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </ScrollArea>

              <DialogFooter className="p-4 bg-gray-50 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsDetailsModalOpen(false)}
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
